import { useDataContext } from "@/context/DataContext";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import PackageSummaryBanner from "@/pages/booking/components/PackageSummaryBanner";
import { getLocalized, isRTL } from "@/utils/localization";

const accentMap: Record<string, { bg: string; text: string; border: string; activeBg: string; activeBorder: string }> = {
  teal:   { bg: "bg-brand-cream-50",  text: "text-brand-forest-600", border: "border-brand-cream-200",  activeBg: "bg-brand-cream-100",  activeBorder: "border-brand-forest-500" },
  orange: { bg: "bg-orange-50",  text: "text-orange-600",  border: "border-orange-100",  activeBg: "bg-orange-100",  activeBorder: "border-orange-400" },
  violet: { bg: "bg-violet-50",  text: "text-violet-600",  border: "border-violet-100",  activeBg: "bg-violet-100",  activeBorder: "border-violet-400" },
  cyan:   { bg: "bg-cyan-50",    text: "text-cyan-600",    border: "border-cyan-100",    activeBg: "bg-cyan-100",    activeBorder: "border-cyan-400" },
  rose:   { bg: "bg-rose-50",    text: "text-rose-600",    border: "border-rose-100",    activeBg: "bg-rose-100",    activeBorder: "border-rose-400" },
  amber:  { bg: "bg-amber-50",   text: "text-amber-600",   border: "border-amber-100",   activeBg: "bg-amber-100",   activeBorder: "border-amber-400" },
  green:  { bg: "bg-green-50",   text: "text-green-600",   border: "border-green-100",   activeBg: "bg-green-100",   activeBorder: "border-green-400" },
};

interface StepServiceProps {
  selected: string;
  onSelect: (id: string) => void;
  onNext: () => void;
  packageId?: string;
}

export default function StepService({ selected, onSelect, onNext, packageId }: StepServiceProps) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const rtl = isRTL(currentLang);
  const { services: rawServices } = useDataContext();
  const servicesData = rawServices.filter((s) => (s as any).status !== "inactive");

  const categories = [
    { id: "all", label: t("booking.step1.filter_all", "الكل") },
    ...Array.from(new Set(servicesData.map((s) => s.category))).map((cat) => {
      const matched = servicesData.find((s) => s.category === cat);
      return {
        id: cat,
        label: getLocalized(currentLang, cat, matched?.category_en),
      };
    }),
  ];
  const [activeCategory, setActiveCategory] = useState("all");

  const selectedIds = selected ? selected.split(",") : [];

  const handleSelect = (id: string) => {
    let newIds: string[];
    if (selectedIds.includes(id)) {
      newIds = selectedIds.filter((x) => x !== id);
    } else {
      newIds = [...selectedIds, id];
    }
    onSelect(newIds.join(","));
  };

  const handleClear = () => {
    onSelect("");
  };

  const filtered = servicesData.filter(
    (s) => activeCategory === "all" || s.category === activeCategory
  );

  return (
    <div>
      {/* Package Banner — shown when coming from a package page */}
      {packageId && <PackageSummaryBanner packageId={packageId} />}

      <div className="mb-6">
        <h2 className="text-xl font-black text-gray-900 mb-1">{t("booking.step1.heading")}</h2>
        <p className="text-gray-400 text-sm">{t("booking.step1.desc")}</p>
      </div>

      {/* Category filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-5 flex-nowrap">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`flex-shrink-0 text-xs font-semibold px-4 py-1.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
              activeCategory === cat.id
                ? "bg-brand-forest-600 text-white"
                : "bg-brand-cream-100 text-gray-600 hover:bg-brand-cream-200"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Services grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[360px] overflow-y-auto pl-1 pb-1">
        {filtered.map((service) => {
          const accent = accentMap[service.accentColor] ?? accentMap.teal;
          const isSelected = selectedIds.includes(service.id);
          const svcName = getLocalized(currentLang, service.name, service.name_en);
          const svcTagline = getLocalized(currentLang, service.tagline, service.tagline_en);
          const svcCategory = getLocalized(currentLang, service.category, service.category_en);

          return (
            <button
              key={service.id}
              onClick={() => handleSelect(service.id)}
              className={`w-full flex items-start gap-3 p-4 rounded-2xl border-2 text-start transition-all duration-200 cursor-pointer group ${
                isSelected
                  ? `${accent.activeBg} ${accent.activeBorder}`
                  : `bg-white ${accent.border} hover:${accent.activeBg} hover:${accent.activeBorder}`
              }`}
            >
              {/* Icon */}
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors ${
                isSelected ? accent.activeBg : accent.bg
              }`}>
                <i className={`${service.icon} text-lg ${accent.text}`}></i>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className={`font-bold text-sm leading-snug ${isSelected ? "text-gray-900" : "text-gray-800"}`}>
                    {svcName.split("—")[0].trim()}
                  </p>
                  {isSelected && (
                    <div className="w-5 h-5 flex items-center justify-center bg-brand-forest-600 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed line-clamp-2">{svcTagline}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${accent.bg} ${accent.text}`}>
                    {svcCategory}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected services summary */}
      {selectedIds.length > 0 && (
        <div className="mt-4 p-4 rounded-2xl border border-brand-cream-300 bg-brand-cream-50/50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-gray-700">{t("booking.step1.selected")} ({selectedIds.length})</span>
            <button
              onClick={handleClear}
              className="text-xs text-red-500 hover:text-red-600 font-semibold cursor-pointer transition-colors"
            >
              {t("booking.step1.clear_all")}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedIds.map((id) => {
              const svc = servicesData.find((s) => s.id === id);
              if (!svc) return null;
              const accent = accentMap[svc.accentColor] ?? accentMap.teal;
              const svcName = getLocalized(currentLang, svc.name, svc.name_en);
              return (
                <div
                  key={id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium ${accent.bg} ${accent.text} ${accent.border}`}
                >
                  <i className={svc.icon}></i>
                  <span>{svcName.split("—")[0].trim()}</span>
                  <button
                    onClick={() => handleSelect(id)}
                    className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-black/5 cursor-pointer text-[10px]"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6">
        <button
          onClick={() => selectedIds.length > 0 && onNext()}
          disabled={selectedIds.length === 0}
          className={`inline-flex items-center gap-2 font-bold px-8 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
            selectedIds.length > 0
              ? "bg-brand-forest-600 hover:bg-brand-forest-700 text-white"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          {t("booking.step1.continue")}
          <i className={rtl ? "ri-arrow-left-line" : "ri-arrow-right-line"}></i>
        </button>
      </div>
    </div>
  );
}
